import { Suspense, useEffect, useRef, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { Tooltip } from "react-tooltip";
import checkOnlineStatus from "./checkOnlineStatus";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PersistSignIn from "./components/PersistSignIn";
import loaderjson from "./utils/mainLoader.json";
import loaderjsonLight from "./utils/mainLoaderLight.json";
import PrivateRoutes from "./utils/PrivateRoutes";
import Lottie from "lottie-react";
import useAxiosPrivate from "./hooks/useAxiosPrivate";
import * as Lazy from "./lazyComponents";
import NewPageIframe from "./utils/NewPageIframe";
import { useDispatch } from "react-redux";
import { changeTheme } from "./features/modeSlice";
import { ROLES } from "./utils";
import PhaserGame from "./components/phaser/Game";

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/vnc_console" element={<NewPageIframe />} />
      <Route path="/" element={<Outlet />}>
        {/* PUBLIC ROUTES */}
        <Route path="legal/:type" element={<Lazy.Main />} />
        <Route
          path="onboarding/:token/:agreed_policies"
          element={<Lazy.OnboardingModal />}
        />
        <Route path="register" element={<Lazy.SignUpPage />} />
        <Route path="signin" element={<Lazy.SignInPage />} />
        <Route path="verified" element={<Lazy.VerificationPage />} />
        <Route path="expired" element={<Lazy.Expired />} />
        <Route path="forget-password" element={<Lazy.ForgetPassword />} />
        <Route
          path="reset-password/:user_id/:token"
          element={<Lazy.ResetPassword />}
        />
        <Route path="account-lockout" element={<Lazy.AccountLockout />} />

        {/* PRIVATE ROUTES */}

        <Route element={<PersistSignIn />}>
          <Route path="" element={<Lazy.BattleGround />}>
            <Route index element={<Navigate to="playground" replace />} />
            <Route path="/help" element={<Lazy.HelpPage />} />
            <Route
              element={
                <PrivateRoutes
                  allowedRoles={[
                    ROLES.Guest,
                    ROLES.Student,
                    ROLES.User,
                    ROLES.Super_admin,
                    ROLES.Manager,
                  ]}
                />
              }
            >
              {/* <Route path="escape" element={<PhaserGame />} /> */}
              <Route path="settings" element={<Lazy.UserSetting />} />
              <Route path="support-ticket" element={<Lazy.SupportTicket />}>
                <Route path=":ticket_id" element={<Lazy.ViewTicket />} />
              </Route>
              <Route path="manageaccess" element={<Lazy.ManageAccess />} />
              <Route path="profile" element={<Lazy.UserProfileMain />}>
                <Route path="overview" element={<Lazy.UserOverView />} />
                <Route path="activity" element={<Lazy.UserActivity />} />
                <Route path="achievement" element={<Lazy.UserStatistics />} />
                <Route
                  path="achievement/:id/:type/:medalType"
                  element={<Lazy.UserStatistics />}
                />
                <Route path="teams" element={<Lazy.UserTeams />}>
                  <Route path="create" element={<Lazy.CreateTeamsForm />} />
                  <Route
                    path="preview/:team_id"
                    element={<Lazy.TeamsCardsDetails />}
                  />
                  <Route
                    path="edit/:team_id"
                    element={<Lazy.CreateTeamsForm />}
                  />
                </Route>
                <Route path="organization" element={<Lazy.UserOrg />} />
              </Route>

              <Route path="playground" element={<Lazy.PlayGround />} />
              <Route path="playground/tracks" element={<Lazy.TracksPage />}>
                <Route path=":trackId" element={<Lazy.CourseCards />}>
                  <Route
                    path=":courseId"
                    element={<Lazy.ModuleCards />}
                  ></Route>
                </Route>
                {/* <Route path=":trackId/statistics" element={<Lazy.TrackStatistics />}/> */}
              </Route>
              <Route
                path="playground/tracks/:trackId/:courseId/statistics"
                element={<Lazy.CourseStatistics />}
              ></Route>
              <Route
                path="playground/tracks/:trackId/:courseId/statistics/:userId"
                element={<Lazy.CourseStatisticUser />}
              />
              <Route
                path="playground/tracks/:trackId/statistics"
                element={<Lazy.TrackStatistics />}
              />
              <Route
                path="playground/tracks/:trackId/statistics/:userId"
                element={<Lazy.TrackStatisticsUser />}
              />
              <Route
                path="playground/standalonemodule"
                element={<Lazy.StandaloneModule />}
              />
              <Route path="playground/cvelabs" element={<Lazy.CvePages />} />
              <Route
                path="playground/module/:moduleId/"
                element={<Lazy.MrRobotCTF />}
              >
                <Route path="result" element={<Lazy.ViewResult />} />
              </Route>
              <Route
                path="playground/module/:moduleId/leaderboard"
                element={<Lazy.LeaderBoard isAdmin={false} />}
              />
              <Route
                path="playground/module/:moduleId/statistics"
                element={<Lazy.Statistics isAdmin={true} />}
              />
              <Route
                path="playground/module/:moduleId/result/:userid"
                element={<Lazy.Stats />}
              />
            </Route>

            <Route
              path="/scenario-arena"
              element={<Lazy.CVEMainPage type="Scenario" />}
            />
            <Route
              path="/scenario-arena/:id"
              element={<Lazy.BrowseScenarioTemplate type="play" />}
            />
            <Route
              path="/scenario-arena/ongoing-scenario-list"
              element={<Lazy.ScenarioListLab type="ongoing" />}
            />
            <Route
              path="/scenario-arena/scenario-list"
              element={<Lazy.ScenarioListLab type="list" />}
            />

            <Route
              path="/cve-arena"
              element={<Lazy.CVEMainPage type="CVE" />}
            />
            <Route
              path="/cve-arena/:cve_id"
              element={<Lazy.CvePlay1 type="CVE_ARENA" />}
            />
            {/* <Route
              path="/cve-arena/ongoing-cve-list"
              element={<Lazy.CveListLab type="ongoing" />}
            /> */}
            <Route path="/cve-arena/cve-list" element={<Lazy.CveListLab />} />
            {/* manage assets */}
            <Route
              element={
                <PrivateRoutes
                  allowedRoles={[ROLES.Super_admin, ROLES.Manager]}
                />
              }
            >
              <Route
                path="/manage/library"
                element={<Lazy.MainManageLibrary />}
              >
                <Route path="assets" element={<Lazy.ManageAssetsHome />}>
                  <Route path="create" element={<Lazy.CreateAssets />} />
                  <Route
                    path="step1/edit/:id"
                    element={<Lazy.CreateAssets />}
                  />
                  <Route
                    path="step2/edit/:id"
                    element={<Lazy.ImageUpload />}
                    type="ImageUpload"
                  />
                </Route>

                <Route path="flavor" element={<Lazy.ManageFlavorHome />} />
              </Route>
            </Route>

            <Route
              path="/dashboard/cve/:cve_id/report"
              element={<Lazy.CVEAdminReport type="CVE" />}
            />
            <Route
              path="/cve-arena/:cve_id/user/:user_id/report"
              element={<Lazy.CVEUserReport type="CVE" />}
            />
            <Route
              path="/cve-arena/:cve_id/report"
              element={<Lazy.CVEUserReport type="CVE" />}
            />
            <Route
              path="/cve-arena/:id/play"
              element={<Lazy.PlaySCenariosCVE type="CVE" />}
            />
            <Route
              path="/scenario-arena/:id/play"
              element={<Lazy.PlaySCenariosCVE type="Scenario" />}
            />

            <Route
              path="/dashboard/scenario/:scenario_id/report"
              element={<Lazy.CVEAdminReport type="Scenario" />}
            />
            <Route
              path="/scenario-arena/:scenario_id/user/:user_id/report"
              element={<Lazy.CVEUserReport type="scenario" />}
            />

            <Route path="unauthorized" element={<Lazy.Forbidden />} />
            <Route
              path="resource/event/:id"
              element={<Lazy.ResourcePage type="event" />}
            />
            <Route
              path="advancesetting/event/:id"
              element={<Lazy.AdvanceSetting type="event" />}
            />
            <Route
              path="advancesetting/scenario/:id"
              element={<Lazy.AdvanceSetting type="scenario" />}
            />
            <Route path="dashboard" element={<Lazy.MainDashboard />}>
              <Route
                element={
                  <PrivateRoutes
                    allowedRoles={[ROLES.Super_admin, ROLES.Manager]}
                  />
                }
              >
                <Route path="bin" element={<Lazy.SoftDeletion />} />
                <Route path="content" element={<Lazy.CreateContent />}>
                  <Route
                    path="achievements/edit/card/:id"
                    element={<Lazy.EditEventCard />}
                  />
                  <Route
                    path="achievements/edit/certificate/:id"
                    element={<Lazy.Certificate />}
                  />

                  <Route
                    path="achievements/edit/badge/:id"
                    element={<Lazy.EditBadge />}
                  />
                  <Route
                    path="create/achievement"
                    element={<Lazy.CreateAchievement />}
                  >
                    <Route path="certificate" element={<Lazy.Certificate />} />
                    <Route
                      path="certificate/:id"
                      element={<Lazy.Certificate />}
                    />
                    <Route path="badge" element={<Lazy.Badges />} />
                    <Route path="card" element={<Lazy.EventCard />} />
                  </Route>

                  <Route
                    path="questionaire"
                    element={<Lazy.CreateQuestionaire />}
                  />
                  {/* this route is for creation */}
                  <Route path=":type/skills/" element={<Lazy.CreateSkill />}>
                    <Route
                      path="skills-list/:id?"
                      element={<Lazy.FullPageOfSkills />}
                    />
                    <Route
                      path="category-list"
                      element={<Lazy.CategoryList />}
                    />
                    <Route
                      path="category-list/:category_id"
                      element={<Lazy.CategoryForm />}
                    />
                    <Route
                      path="create-category"
                      element={<Lazy.CategoryForm />}
                    />
                    <Route path="create-skill" element={<Lazy.SkillForm />} />
                    <Route path=":skill_id" element={<Lazy.SkillForm />} />
                  </Route>
                  <Route
                    path="question/:question_id"
                    element={<Lazy.CreateQuestionaire />}
                  />
                  {/* this route is for updation */}
                  <Route
                    path=":type/:question_id/skills/"
                    element={<Lazy.CreateSkill />}
                  >
                    <Route
                      path="skills-list/:id?"
                      element={<Lazy.FullPageOfSkills />}
                    />
                    <Route
                      path="category-list"
                      element={<Lazy.CategoryList />}
                    />
                    <Route
                      path="category-list/:category_id"
                      element={<Lazy.CategoryForm />}
                    />
                    <Route
                      path="create-category"
                      element={<Lazy.CategoryForm />}
                    />
                    <Route path="create-skill" element={<Lazy.SkillForm />} />
                    <Route path=":skill_id" element={<Lazy.SkillForm />} />
                  </Route>
                  <Route
                    path="questionaire/:questionaire_id"
                    element={<Lazy.CreateQuestionaire />}
                  />
                  <Route path="track" element={<Lazy.CreateTrack />}>
                    <Route path="" element={<Lazy.CreateTrackStep1 />} />
                    <Route
                      path="preview/:track_id"
                      element={<Lazy.CreateTrackStep1 />}
                    />
                    <Route
                      path=":track_id"
                      element={<Lazy.CreateTrackStep1 />}
                    />
                    <Route
                      path="privacy/:track_id"
                      element={<Lazy.CreateTrackstep3 />}
                    />
                    <Route
                      path="preview/privacy/:track_id"
                      element={<Lazy.CreateTrackstep3 />}
                    />
                    <Route
                      path="add-course/:track_id"
                      element={<Lazy.AddCourcesInTrack />}
                    />
                    <Route
                      path="preview/add-course/:track_id"
                      element={<Lazy.AddCourcesInTrack />}
                    />
                  </Route>

                  <Route path="course" element={<Lazy.CreateCourses />}>
                    <Route path="" element={<Lazy.CreateCourseStep1 />} />
                    <Route
                      path=":course_id"
                      element={<Lazy.CreateCourseStep1 />}
                    />
                    <Route
                      path="preview/:course_id"
                      element={<Lazy.CreateCourseStep1 />}
                    />
                    <Route
                      path="privacy/:course_id"
                      element={<Lazy.CreateCourseStep3 />}
                    />
                    <Route
                      path="preview/privacy/:course_id"
                      element={<Lazy.CreateCourseStep3 />}
                    />
                    <Route
                      path="add-module/:course_id"
                      element={<Lazy.AddModulesInCourse />}
                    />
                    <Route
                      path="preview/add-module/:course_id"
                      element={<Lazy.AddModulesInCourse />}
                    />
                  </Route>
                  <Route path="module" element={<Lazy.CreateModule />}>
                    <Route path="" element={<Lazy.CreateModuleStep1 />} />
                    <Route
                      path="preview/:id"
                      element={<Lazy.CreateModuleStep1 />}
                    />
                    <Route
                      path="preview/:id"
                      element={<Lazy.PreviewModule />}
                    />
                    <Route path=":id" element={<Lazy.CreateModuleStep1 />} />
                    <Route
                      path="add-content/:id"
                      element={<Lazy.CreateModuleStep2 />}
                    />
                    <Route
                      path="preview/add-content/:id"
                      element={<Lazy.CreateModuleStep2 />}
                    />
                    <Route
                      path="privacy/:id"
                      element={<Lazy.CreateModuleStep3 />}
                    />
                    <Route
                      path="preview/privacy/:id"
                      element={<Lazy.CreateModuleStep3 />}
                    />
                  </Route>
                </Route>
                <Route path="management" element={<Lazy.AccessManagement />}>
                  <Route
                    path=":orgId"
                    element={<Lazy.OrganizationPermission />}
                  />
                  <Route
                    path="edit-user/:user_id"
                    element={<Lazy.EditProfile />}
                  />
                  <Route path="teams/:orgId" element={<Lazy.Teams />}>
                    <Route path="create" element={<Lazy.CreateTeamsForm />} />
                    <Route
                      path="edit/:team_id"
                      element={<Lazy.CreateTeamsForm />}
                    />
                  </Route>
                  <Route path="organization" element={<Lazy.Organization />} />
                </Route>
                <Route path="announcement" element={<Lazy.Announcement />}>
                  <Route path="create/" element={<Lazy.CreateAnn />} />
                  <Route
                    path="create/:active_id"
                    element={<Lazy.CreateAnn />}
                  />
                  <Route
                    path="create/:active_id/privacy"
                    element={<Lazy.CreateAnn />}
                  />
                  <Route
                    path="review/:active_id"
                    element={<Lazy.CreateAnn />}
                  />
                </Route>
                <Route path="cve" element={<Lazy.CveLandingPage />}>
                  <Route path="" element={<Lazy.CveList />} />
                  <Route path="edit/:id" element={<Lazy.CreateCve />} />
                  <Route
                    path="preview/:id"
                    element={<Lazy.CvePlay1 type="CVE_ARENA" />}
                  />
                  <Route
                    path=":cve_id"
                    element={<Lazy.CvePlay1 type="CVE" />}
                  />

                  <Route path="create" element={<Lazy.CreateCve />} />
                </Route>

                <Route path="scenario" element={<Lazy.Scenario />}>
                  <Route path="" element={<Lazy.CreateScenario />} />
                  <Route
                    path="create/step1"
                    element={<Lazy.CreateScenarioStep1 />}
                  />
                  <Route
                    path="create/browse"
                    element={<Lazy.BrowseScenario />}
                  />
                  <Route
                    path="create/browse/:id"
                    element={<Lazy.BrowseScenarioTemplate type="create" />}
                  />
                  <Route
                    path="edit/step1/:id"
                    element={<Lazy.CreateScenarioStep1 />}
                  />
                  <Route
                    path="edit/step2/:id"
                    element={<Lazy.CreateScenarioStep2 />}
                  />
                  <Route
                    path="create/step2/:id"
                    element={<Lazy.CreateScenarioStep2 />}
                  />
                </Route>
                <Route path="event" element={<Lazy.CreateEventExercise />}>
                  <Route
                    path="preview_event/view/:id"
                    element={<Lazy.EventHomeView />}
                  />
                  <Route path="create" element={<Lazy.CreateEvents />} />
                  <Route path="edit/:id" element={<Lazy.CreateEvents />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<Lazy.PageNotFound />} />

            <Route path="/pagenotfound" element={<Lazy.PageNotFound />} />
            <Route
              path="/somethingwentwrong"
              element={<Lazy.SomethingWentWrong />}
            />
            {/* <Route path="/error" element={<Lazy.InternalServerError />} /> */}
            <Route
              element={
                <PrivateRoutes
                  allowedRoles={[ROLES.Super_admin, ROLES.User, ROLES.Manager]}
                />
              }
            >
              <Route path="event-arena" element={<Lazy.EventArena />}>
                <Route path="view/:event_id" element={<Lazy.JoinEvent />} />
                <Route path="event/:event_id" element={<Lazy.Event />}>
                  <Route index element={<Lazy.Exercise />} />

                  <Route path="exercise" element={<Lazy.Exercise />} />
                  <Route path="statistic" element={<Lazy.Statistic />} />
                  <Route
                    path="statistic/:userid"
                    element={<Lazy.Statistic />}
                  />
                  <Route path="resources/" element={<Lazy.EventResources />} />
                  <Route
                    path="leaderboard"
                    element={<Lazy.EventLeaderBoard />}
                  />
                </Route>
              </Route>
            </Route>
            <Route
              element={
                <PrivateRoutes allowedRoles={[ROLES.Super_admin, ROLES.User]} />
              }
            >
              <Route path="report" element={<Lazy.Report />} />
            </Route>
          </Route>
        </Route>

        {/* CATCH ALL */}

        {/* <Route path="*" element={<Forbidden />} /> */}
        {/* <Route path="*" element={<SomethingWentWrong />} /> */}
        {/* <Route path="*" element={<InternalServerError />} /> */}
      </Route>
    </>,
  ),
);
var currentRole = null;

function useAutoTheme() {
  const [theme, setTheme] = useState(localStorage.getItem("mode") || "light");
  const pollRef = useRef(null);

  useEffect(() => {
    const detect = () => {
      const ls = localStorage.getItem("mode");
      if (ls === "dark" || ls === "light") return ls;
      if (document.body.classList.contains("dark")) return "dark";
      if (window.matchMedia("(prefers-color-scheme: dark)").matches)
        return "dark";
      return "light";
    };

    const update = () => {
      const t = detect();
      setTheme((prev) => (prev === t ? prev : t));
    };

    // cross-tab sync
    const onStorage = (e) => {
      if (e.key === "mode") update();
    };
    window.addEventListener("storage", onStorage);

    // custom event for same-tab triggers
    const onCustom = (e) => {
      const t = e?.detail?.theme;
      if (t === "dark" || t === "light") setTheme(t);
      else update();
    };
    window.addEventListener("themeChange", onCustom);

    // fallback polling
    pollRef.current = setInterval(update, 800);

    // initial check
    update();

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("themeChange", onCustom);
      clearInterval(pollRef.current);
    };
  }, []);

  return theme;
}

export default function App() {
  const axiosPrivate = useAxiosPrivate();
  const online = checkOnlineStatus();
  const dispatch = useDispatch();

  const theme = useAutoTheme();

  useEffect(() => {
    if (theme === "dark") {
      document.body.classList.add("dark");
      document.body.classList.remove("light");
      localStorage.setItem("mode", "dark");
      // dispatch(changeTheme({ mode: "dark" }));
    } else {
      document.body.classList.add("light");
      document.body.classList.remove("dark");
      localStorage.setItem("mode", "light");
      // dispatch(changeTheme({ mode: "light" }));
    }
  }, [theme, dispatch]);

  const animationData = theme === "dark" ? loaderjsonLight : loaderjson;
  const tooltipColor = theme === "dark" ? "#3E516E" : "#3a3b40";

  function useScreenSize() {
    const [size, setSize] = useState({
      width: window.innerWidth,
      height: window.innerHeight,
    });

    useEffect(() => {
      const handleResize = () => {
        setSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    return size;
  }

  const { width, height } = useScreenSize();

  return (
    <>
      {online ? (
        <>
          {/* <p>Window Width: {width}px</p>
      <p>Window Height: {height}px</p> */}
          <ToastContainer
            autoClose={2000}
            hideProgressBar={true}
            limit={1}
            className="z-[999999999] w-full"
          />
          <Tooltip
            id="showMe"
            style={{
              background: tooltipColor,
              zIndex: 9999999,
              fontSize: "11px",
              color: "#fff",
              maxWidth: "250px",
              wordWrap: "break-word",
            }}
          />

          <Suspense
            fallback={
              <div className="fixed inset-0 z-[9999] bg-maastrichtBlue dark:bg-lmaastrichtBlue opacity-75 flex justify-center items-center">
                <div className="z-[99999]">
                  <Lottie
                    animationData={animationData}
                    loop={true}
                    style={{ width: 200, height: 200 }}
                  />
                </div>
              </div>
            }
          >
            <RouterProvider router={router} axiosPrivate={axiosPrivate} />
          </Suspense>
        </>
      ) : (
        <div
          className="flex flex-col items-center justify-center text-center"
          style={{ minHeight: "100vh" }}
        >
          {/* No internet SVG */}
          <svg
            width="200"
            height="200"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="32"
              cy="32"
              r="30"
              stroke="#D32F2F"
              strokeWidth="4"
              fill="#fff"
            />
            <path
              d="M18 26c4-4 9.5-6.5 15-6.5s11 2.5 15 6.5"
              stroke="#555"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M22 30c2.8-2.8 6.2-4.2 10-4.2s7.2 1.4 10 4.2"
              stroke="#555"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M26 34c1.5-1.5 3.3-2.3 5.3-2.3s3.8.8 5.3 2.3"
              stroke="#555"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="32" cy="44" r="2.5" fill="#D32F2F" />
            <line
              x1="16"
              y1="16"
              x2="48"
              y2="48"
              stroke="#D32F2F"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>

          <div className="mt-4 text-red-600 font-semibold text-lg">
            No internet connection detected. Please verify your network.
          </div>
        </div>
      )}
    </>
  );
}
